import type {ReactNode} from 'react'
import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Dashboard} from '@freckle/school/ts/common/components/dashboard'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import {Middleware} from '@freckle/school/ts/common/routers/middleware'
import {useSchoolAdmin} from '@freckle/school/ts/hooks/use-school-admin'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'

export type ConfigurableWrapperProps = {
  tabId?: string
  fullScreen?: boolean
}

type Props = {
  children: ReactNode
} & ConfigurableWrapperProps

export default function Wrapper(props: Props) {
  const {tabId, fullScreen, children} = props

  const schools = useSchools()
  const schoolAdmin = useSchoolAdmin()

  return (
    <Middleware>
      <WithResources
        resources={{schools, schoolAdmin}}
        render={({schools, schoolAdmin}) => (
          <Inner
            tabId={tabId ?? ''}
            fullScreen={fullScreen}
            schoolAdmin={schoolAdmin}
            schools={schools}
          >
            {children}
          </Inner>
        )}
      />
    </Middleware>
  )
}

type InnerProps = {
  tabId: string
  fullScreen: boolean | undefined
  schools: Array<SchoolAttrs>
  schoolAdmin: SchoolAdminAttrs
  children: ReactNode
}

const Inner = (props: InnerProps) => {
  const {tabId, fullScreen, schools, schoolAdmin, children} = props
  const {i18n} = useTranslation()

  const wrapperBody =
    fullScreen === true ? (
      children
    ) : (
      <Dashboard tabId={tabId} schools={schools} schoolAdmin={schoolAdmin}>
        {children}
      </Dashboard>
    )

  useEffect(() => {
    const checkAndSetLanguage = async () => {
      if (i18n.language !== schoolAdmin.language) {
        try {
          await i18n.changeLanguage(schoolAdmin.language)
        } catch (err) {
          console.log('could not load language data', err)
        }
      }
    }
    checkAndSetLanguage()
  }, [i18n, i18n.language, schoolAdmin.language])

  return wrapperBody
}
